import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureInteger } from '../common/utils/generalUtils';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareContactList = (l1, l2) => ensureInteger(l1.id) === ensureInteger(l2.id);
const comapreContact = (c1, c2) => ensureInteger(c1.id) === ensureInteger(c2.id);
const compareContactMapping = (m1, m2) => m1.contactid === m2.contactid && m1.listid === m2.listid && m1.type === m2.type;

const contactSlice = createSlice({
    name: 'contacts',
    initialState: {
        lists: [],
        contacts: [],
        mappings: []
    },
    reducers: {
        setLists: (state, action) => {
            state.lists = action.payload ?? []
        },
        setContacts: (state, action) => {
            state.contacts = action.payload ?? []
        },
        setContactMappings: (state, action) => {
            if (action.payload.contactid) {
                state.mappings = state.mappings.filter(m => m.contactid !== action.payload.contactid)
                state.mappings = state.mappings.concat(action.payload.data.map(m => ({ contactid: action.payload.contactid, listid: m.listid, type: m.type })));
            }
        },
        addOrUpdateList: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.lists.findIndex(l2 => compareContactList(l, l2));
                if (idx >= 0) {
                    state.lists.splice(idx, 1, l);
                } else {
                    state.lists.push(l);
                }
            }
        },
        removeList: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.lists.findIndex(l2 => compareContactList(l2, l));
                if (idx >= 0) {
                    state.lists.splice(idx, 1);
                }
                for (let i = state.mappings.findIndex(m => m.listid === l.id); i > -1; i = state.mappings.findIndex(m => m.listid === l.id)) {
                    state.mappings.splice(i, 1);
                }
            }
        },
        addOrUpdateContact: (state, action) => {
            for (const c of ensureArray(action.payload)) {
                const listid = c.listid;
                const type = c.type;
                const toAdd = {...c};
                delete toAdd.listid;
                delete toAdd.type;
                delete toAdd.value;
                let idx = state.contacts.findIndex(c2 => comapreContact(toAdd, c2));
                if (idx >= 0) {
                    state.contacts.splice(idx, 1, toAdd);
                } else {
                    state.contacts.push(toAdd);
                }
                if (listid && type) {
                    let toAdd = { listid, type, contactid: c.id };
                    idx = state.mappings.findIndex(m => compareContactMapping(m, toAdd));
                    if (idx >= 0) {
                        state.mappings.splice(idx, 1, toAdd);
                    } else {
                        state.mappings.push(toAdd);
                    }
                }
            }
        },
        removeContact: (state, action) => {
            for (const c of ensureArray(action.payload)) {
                let idx = state.contacts.findIndex(c2 => compareContactList(c2, c));
                if (idx >= 0) {
                    state.contacts.splice(idx, 1)
                }
                for (let i = state.mappings.findIndex(m => m.contactid === c.id); i > -1; i = state.mappings.findIndex(m => m.contactid === c.id)) {
                    state.mappings.splice(i, 1);
                }
            }
        },
        addOrUpdateMapping: (state, action) => {
            for (const m of ensureArray(action.payload)) {
                let idx = state.mappings.findIndex(m2 => compareContactMapping(m, m2));
                if (idx >= 0) {
                    state.mappings.splice(idx, 1, m);
                } else {
                    state.mappings.push(m);
                }
            }
        },
        removeMapping: (state, action) => {
            for (const m of ensureArray(action.payload)) {
                let idx = state.mappings.findIndex(m2 => compareContactMapping(m, m2));
                if (idx >= 0) {
                    state.mappings.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => contactSlice.getInitialState());
    }
})

// #region network oprations
const fetchContactLists = network => network.get('/api/v1/contactLists', contactSlice.actions.setLists);
const fetchAllContacts = network => network.get('/api/v1/contacts', contactSlice.actions.setContacts);
export const createContact = (network, contact) => network.post('/api/v1/contact', contact, contactSlice.actions.addOrUpdateContact);
export const updateContact = (network, contact) => network.put('/api/v1/contact', contact, contactSlice.actions.addOrUpdateContact);
export const removeContact = (network, contact) => network.delete('/api/v1/contact/' + encodeURIComponent(contact.id), contactSlice.actions.removeContact);

const fetchListContacts = listid => network => network.get('/api/v1/contacts/' + encodeURIComponent(listid), contactSlice.actions.addOrUpdateContact);
const fetchContactMappings = contactid => network => network.get('/api/v1/contactmapping/' + encodeURIComponent(contactid), data => contactSlice.actions.setContactMappings({ contactid, data }));
export const addContactListMapping = (network, contactid, listid, type) => network.post('/api/v1/contactmapping', { contactid, listid, type }, contactSlice.actions.addOrUpdateMapping);
export const removeContactListMapping = (network, contactid, listid, type) => network.delete(`/api/v1/contactmapping/${encodeURIComponent(contactid)}/${encodeURIComponent(listid)}/${encodeURIComponent(type)}`, contactSlice.actions.removeMapping);

export const createContactList = (network, list) => network.post('/api/v1/contactList', list, contactSlice.actions.addOrUpdateList);
export const updateContactList = (network, list) => network.put('/api/v1/contactList', list, contactSlice.actions.addOrUpdateList);
export const removeContactList = (network, list) => network.delete('/api/v1/contactList/' + encodeURIComponent(list.id), contactSlice.actions.removeList);
// #endregion

// #region permission checks
export const canCreateList = (credentials) => credentials.access.hasFull('administrator');
export const canUpdateList = (credentials, list) => credentials.access.hasFull('administrator');
export const canRemoveList = (credentails, list) => credentails.access.hasFull('administrator');

export const canCreateContact = (credentials) => credentials.access.hasFull('administrator');
export const canUpdateContact = (credentials, contact) => credentials.access.hasFull('administrator');
export const canRemoveContact = (credentails, contact) => credentails.access.hasFull('administrator');


// #endregion

// #region selector
const selectAllLists = state => state.contacts.lists;
const selectContactListById = createSelector([selectAllLists, (lists, id) => id], (lists, id) => lists.find(l => l.id == id));
const selectContactList = id => state => selectContactListById(state, id);

const selectAllContacts = state => state.contacts.contacts;
const selectContactById = createSelector([selectAllContacts, (contacts, id) => id], (contacts, id) => contacts.find(c => c.id == id));
const selectContact = id => state => selectContactById(state, id);


const selectListContacts = state => ({ contacts: state.contacts.contacts, mappings: state.contacts.mappings });
const selectContactsByList = createSelector([selectListContacts, (data, listid) => listid], ({ contacts, mappings}, listid) => {
    return mappings.reduce((result, m) => {
        if (m.listid === listid) {
            const contact = contacts.find(c => c.id === m.contactid);
            if (contact) {
                result.push({ ...contact, type: m.type });
            }
        }
        return result;
    }, []);
});
const selectContactsForList = listid => state => selectContactsByList(state, listid);
const selectContactLists = state => ({ mappings: state.contacts.mappings, lists: state.contacts.lists });
const selectListsByContact = createSelector([selectContactLists, (data, contactid) => contactid], ({ mappings, lists }, contactid) => {
    return mappings.reduce((result, m) => {
        if (m.contactid === contactid) {
            const list = lists.find(l => l.id === m.listid);
            if (list) {
                result.push({ ...list, type: m.type });
            }
        }
        return result;
    }, []);
});
const selectListsForContact = contactid => state => selectListsByContact(state, contactid);
// #endregion

// #region listeners
const listenContactList = {
    contactListCreated: (list, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateList(list)),
    contactListUpdated: (list, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateList(list)),
    contactListDeleted: (list, { dispatch }) => dispatch(contactSlice.actions.removeList(list)),
    contactMappingCreated: (c, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateMapping(c)),
    contactmappingDeleted: (c, { dispatch }) => dispatch(contactSlice.actions.removeContact(c))
}

const listenContact = {
    contactCreated: (c, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateContact(c)),
    contactUpdated: (c, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateContact(c)),
    contactDeleted: (c, { dispatch }) => dispatch(contactSlice.actions.removeContact(c)),
    contactMappingCreated: (c, { dispatch }) => dispatch(contactSlice.actions.addOrUpdateMapping(c)),
    contactmappingDeleted: (c, { dispatch }) => dispatch(contactSlice.actions.removeContact(c))
}
// #end region

export const [useContactLists, realizeContactLists] = dataHookFactory(selectAllLists, { run: fetchContactLists, listen: listenContactList});
export const [useContactList] = dataHookFactory(selectContactList, id => ({ run: fetchContactLists, listen: listenContactList}));
export const [useContacts] = dataHookFactory(selectAllContacts, { run: fetchAllContacts, listen: listenContact });
export const [useContact] = dataHookFactory(selectContact, id => ({ run: fetchAllContacts, listen: listenContact }));
export const [useListContacts] = dataHookFactory(selectContactsForList, initMapFactory(fetchListContacts, { ...listenContact, ...listenContactList } ));
export const [useListsForContact] = dataHookFactory(selectListsForContact, initMapFactory(fetchContactMappings, { ...listenContact, ...listenContactList }));

const reducerDetails = { name: contactSlice.name, reducer: contactSlice.reducer };
export default reducerDetails;